<svelte:options
  customElement={{
    tag: "oc-back-to-top-button-v1",

    extend: window.__components.extend(),
    props: {},
  }}
/>

<script lang="ts">
  import { onMount, onDestroy } from "svelte";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { selector } from "./config";

  let mainContainer = $state<HTMLElement>();
  let buttonElement = $state<HTMLElement>();
  let popoverElement = $state<HTMLOcPopoverV1Element>();
  let visible = $state(false);

  let resizeObserver: ResizeObserver | null = null;
  let ticking = false;
  let lastY = 0; /*                             */

  const outerSpacingX = 24;
  /*                                                                                                                                                                         */
  /*                                                   */
  const xlBreakpointQuery = window.matchMedia("(min-width: 62em)");
  /*                                                                                                                                                                         */
  /*                                                    */
  const xxlBreakpointQuery = window.matchMedia("(min-width: 77em)");

  function evaluate(): void {
    const y = window.scrollY || document.documentElement.scrollTop;
    const scrolledDown = y > lastY;
    const scrolledUp = y < lastY;
    const scrolledPastThreshold = y > window.innerHeight; /*                                 */

    /*                                                */
    if (xlBreakpointQuery.matches) {
      /*                                                   */
      if (scrolledDown && scrolledPastThreshold) {
        visible = true;
      } else if (!scrolledPastThreshold) {
        /*                                              */
        visible = false;
      }
    } else {
      /*                                                                */
      if (scrolledUp && scrolledPastThreshold) {
        visible = true;
      } else if (scrolledDown || (scrolledUp && !scrolledPastThreshold)) {
        /*                                   */
        visible = false;
      }
    }

    lastY = y;
  }

  function updateDynamicLeft(): void {
    if (!buttonElement || !mainContainer) return;

    if (!xxlBreakpointQuery.matches) {
      /*                                                          */
      buttonElement.style.removeProperty("--back-to-top-dynamic-left");
      return;
    }

    const left = mainContainer.getBoundingClientRect().right + outerSpacingX;
    buttonElement.style.setProperty("--back-to-top-dynamic-left", `${left}px`);
  }

  function onScroll(): void {
    if (!ticking) {
      requestAnimationFrame(() => {
        evaluate();
        /*                                                                            */
        if (xxlBreakpointQuery.matches) updateDynamicLeft();
        ticking = false;
      });
      ticking = true;
    }
  }

  function scrollToTop(): void {
    window.scrollTo({
      top: 0,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
  }

  function setupObservers(): void {
    mainContainer = document.querySelector(selector) as HTMLElement;
    if (!mainContainer) return;

    resizeObserver = new ResizeObserver(() => () => {
      visible = false; /*                                */
      updateDynamicLeft();
    });
    resizeObserver.observe(mainContainer);
    window.addEventListener(
      "resize",
      () => {
        visible = false; /*                                */
        updateDynamicLeft();
      },
      {
        passive: true,
      },
    );

    xxlBreakpointQuery.addEventListener(
      "change",
      () => {
        visible = false; /*                                */
        updateDynamicLeft();
      },
      {
        passive: true,
      },
    );

    updateDynamicLeft();
  }

  onMount(() => {
    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    setupObservers();
    return () => {
      if (resizeObserver && mainContainer) {
        resizeObserver.unobserve(mainContainer);
        resizeObserver.disconnect();
      }
      window.removeEventListener("resize", updateDynamicLeft);
      xxlBreakpointQuery.removeEventListener("change", updateDynamicLeft);
    };
  });

  onDestroy(() => {
    window.removeEventListener("scroll", onScroll);
    xxlBreakpointQuery.removeEventListener("change", updateDynamicLeft);
  });
</script>

<oc-icon-button-v3
  bind:this={buttonElement}
  variant="secondary-on-color"
  elevation-level="sticky"
  size={/*                      */ "100"}
  icon="arrow-long-up"
  oc-aria-label="Nach oben scrollen"
  onclick={scrollToTop}
  class:is-visible={visible}
  tabindex="-1"
  aria-hidden={!visible}
  inert={!visible}
>
</oc-icon-button-v3>

<oc-popover-v1 variant="tooltip" position="top" anchor={buttonElement} bind:this={popoverElement}>
  Nach oben scrollen
</oc-popover-v1>

<DefaultStyles />

<style lang="scss">
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "@otto-ec/otto-components-utils/scss/mixins";
  :global {
    oc-icon-button-v3 {
      opacity: 0;
      pointer-events: none;
      position: fixed;
      top: tokens.$oc-component-back-to-top-button-outer-spacing-y;
      transform: translateX(-50%);
      transition: opacity tokens.$oc-component-back-to-top-button-appear-duration
        tokens.$oc-component-back-to-top-button-appear-easing;
      z-index: 3000; /*                                         */

      &.is-visible {
        opacity: 1;
        pointer-events: auto;
      }
    }

    @include mixins.breakpoint-from-s(false, false) {
      oc-icon-button-v3 {
        left: 50%;
      }
    }

    @include mixins.breakpoint-from-l(false, false) {
      oc-icon-button-v3 {
        left: auto;
        right: tokens.$oc-component-back-to-top-button-outer-spacing-x;
        top: tokens.$oc-component-back-to-top-button-outer-spacing-y;
        transform: none;
      }
    }

    @include mixins.breakpoint-from-xxl(false) {
      oc-icon-button-v3 {
        bottom: tokens.$oc-component-back-to-top-button-outer-spacing-y;
        left: var(--back-to-top-dynamic-left, auto);
        right: auto;
        top: auto;
      }
    }
  }
</style>
